import type { BlockRule, HttpMethod, PathMatchKind } from '@bartizo/shared';

/**
 * Block rules in words and the presets the rule dialog offers (P11.5). Apart from
 * the components so React's fast refresh keeps working on them.
 */

export interface PathRow {
  kind: PathMatchKind;
  value: string;
}

export interface Draft {
  kind: 'request' | 'behaviour' | 'trigger';
  id: string;
  name: string;
  site: string;
  enabled: boolean;
  mode: 'enforce' | 'watch';
  paths: PathRow[];
  methods: HttpMethod[];
  response: 'forbidden' | 'drop';
  statuses: string;
  count: string;
  within: string;
  ban: string;
  /** A trigger's scenarios, one to a line (P11.16). */
  scenarios: string;
  /** What a trigger does when its scenario fires. */
  triggerResponse: 'ban' | 'captcha';
}

export const BAN_LENGTHS: readonly { seconds: number; label: string }[] = [
  { seconds: 15 * 60, label: '15 minutes' },
  { seconds: 60 * 60, label: '1 hour' },
  { seconds: 4 * 60 * 60, label: '4 hours' },
  { seconds: 24 * 60 * 60, label: '1 day' },
  { seconds: 7 * 24 * 60 * 60, label: '7 days' },
  { seconds: 30 * 24 * 60 * 60, label: '30 days' },
];

export const banLabel = (seconds: number): string =>
  BAN_LENGTHS.find((each) => each.seconds === seconds)?.label ??
  `${String(Math.round(seconds / 60))} minutes`;

/** Common rules, a click from ready: what scanners do on most fleets. */
export const PRESETS: readonly { label: string; hint: string; draft: Partial<Draft> }[] = [
  {
    label: 'Secret files',
    hint: 'Drop requests for .env, .git and other files no site should serve.',
    draft: {
      kind: 'request',
      id: 'secret-files',
      name: 'Secret files',
      response: 'drop',
      paths: [
        { kind: 'exact', value: '/.env' },
        { kind: 'prefix', value: '/.git/' },
        { kind: 'prefix', value: '/.aws/' },
        { kind: 'prefix', value: '/.ssh/' },
      ],
    },
  },
  {
    label: 'Slow SSH guessing',
    hint: 'CrowdSec only reports slow SSH password guessing; ban the address for an hour. Starts in watch mode.',
    draft: {
      kind: 'trigger',
      id: 'ssh-slow',
      name: 'Slow SSH guessing',
      mode: 'watch',
      scenarios: 'crowdsecurity/ssh-time-based-bf',
      triggerResponse: 'ban',
      ban: '3600',
    },
  },
  {
    label: 'PHP scanners',
    hint: 'Ban an address that gets 20 "not found" answers for .php paths in a minute. It counts 404s only: a site that redirects unknown pages never matches it, so use Known exploit files there.',
    draft: {
      kind: 'behaviour',
      id: 'php-scanners',
      name: 'PHP scanners',
      paths: [{ kind: 'suffix', value: '.php' }],
      statuses: '404',
      count: '20',
      within: '60',
    },
  },
  {
    label: 'Known exploit files',
    hint: 'Ban an address that asks for 5 well-known web-shell and probe files in a minute, whatever the site answers. For sites that redirect unknown pages instead of answering 404. Starts in watch mode.',
    draft: {
      kind: 'behaviour',
      id: 'exploit-files',
      name: 'Known exploit files',
      mode: 'watch',
      paths: [
        '/wp_filemanager.php',
        '/shell.php',
        '/c99.php',
        '/r57.php',
        '/wso.php',
        '/alfa.php',
        '/1.php',
        '/up.php',
        '/uploader.php',
        '/adminer.php',
        '/phpinfo.php',
        '/info.php',
      ].map((value) => ({ kind: 'suffix' as const, value })),
      statuses: '',
      count: '5',
      within: '60',
    },
  },
  {
    label: 'WordPress login guessing',
    hint: 'Ban an address that posts to the login 10 times in a minute.',
    draft: {
      kind: 'behaviour',
      id: 'wp-login',
      name: 'WordPress login guessing',
      paths: [
        { kind: 'exact', value: '/wp-login.php' },
        { kind: 'exact', value: '/xmlrpc.php' },
      ],
      methods: ['POST'],
      count: '10',
      within: '60',
    },
  },
];

/** `PHP scanners` → `php-scanners`: the id a new rule gets from its name. */
export const slugOf = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

/** What a rule does, in one line. */
export function describeRule(rule: BlockRule): string {
  if (rule.kind === 'trigger') {
    const names = rule.scenarios.map((name) => name.slice(name.indexOf('/') + 1)).join(', ');
    return `${names} fires → ${rule.response === 'captcha' ? 'challenge' : 'ban'} ${banLabel(rule.ban_seconds)}`;
  }
  const paths = rule.paths
    .map((path) =>
      path.kind === 'exact'
        ? path.value
        : path.kind === 'prefix'
          ? `${path.value}*`
          : `*${path.value}`,
    )
    .join(', ');
  const methods = rule.methods.length === 0 ? '' : `${rule.methods.join('/')} `;
  if (rule.kind === 'request') {
    return `${methods}${paths} → ${rule.response === 'drop' ? 'drop (444)' : 'refuse (403)'}`;
  }
  const statuses = rule.statuses.length === 0 ? '' : ` answered ${rule.statuses.join('/')}`;
  return `${String(rule.count)} × ${methods}${paths === '' ? 'any path' : paths}${statuses} in ${String(rule.within_seconds)}s → ban ${banLabel(rule.ban_seconds)}`;
}
